import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, change } from 'redux-form';
import { t } from 'i18next';
import AddressComplete from '../../ReactComponent/AddressComplete';
import RenderField from '../../ReactComponent/RenderField';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import Plus from '../../../assets/icons/Plus.svg';
import Delete from '../../../assets/icons/RemoveOrange.svg';
import CheckMarkSolidOrange from '../../../assets/icons/CheckMarkSolidOrange.svg';
import Edit from '../../../assets/icons/Edit.svg';
import PasswordGroup from '../../ReactComponent/PasswordGroup';
import {
  isRequired, isPhone, isEmail, maxChar50,
  maxChar40,
  maxChar5,
  maxChar20,
} from '../../../utils/validator';
import { phoneNumber, digitOnly } from '../../../utils/mask';
import {
  AddItemContainer,
  PageTitle,
  ContentBoxLightGrey,
  CategoryTitle,
  CardHeader,
  CardTitle,
  CheckboxContainer,
  FileUploadLabel,
  FileUploadContainer,
  FileUpload,
  FormFields,
  ItemContainer,
  Icon,
  EditButtonGroup,
  LineButton,
  TextProperty,
} from '../css';
import { H3, P } from '../../../theme/typography';
import { BillingAccountType } from '../../../utils/enums';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import Tooltip from '../../../lib/ui/form_elements/Tooltip';
import PasswordInput from '../../../lib/ui/form_elements/PasswordInput';
import PrimaryButton from '../../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../../lib/ui/buttons/SecondaryButton';

const companyCheckboxRenderer = ({ item }) => (
  <H3>
    {t(item.accountNumber || item.permissionName)}
    {' '}
    {item.accountType === 'PA' && (
    <>
      (
      {t('Protected Billing Account')}
      )
    </>
    )}
  </H3>
);

const renderUserCompanyList = ({
  // eslint-disable-next-line no-unused-vars
  fields, dispatch, formName, formData, companiesList, currentCompaniesList, getCompanyInfo, companyDetails, showAdd, error, onRemoveCompany,
}) => {
  const disableAddItem = !!error.companies;
  const addItem = (index) => {
    dispatch(change(formName, `companies[${index}].completed`, true));
  };

  const cancelItem = (index) => {
    dispatch(change(formName, `companies[${index}]`, {}));
  };

  const [state, setState] = useState({
    showAddLineButton: false,
    hideRemoveButton: true,
  });

  useEffect(() => {
    const hideRemoveButton = currentCompaniesList.length <= 1;
    const showAddLineButton = !(currentCompaniesList.some((m) => !m.completed));
    setState({
      ...state,
      hideRemoveButton,
      showAddLineButton,
    });
  }, [currentCompaniesList]);
  const editUser = (index, dispatch) => {
    dispatch(
      change(
        formName,
        `companies[${index}].completed`,
        false,
      ),
    );
  };

  const availableCompanies = companiesList.filter((c) =>
    !currentCompaniesList.some(
      (cc) =>
        cc.companyId
        && cc.completed
        && cc.companyId === c.companyId,
    ),
  );

  const disableAddAnother = availableCompanies.length === 0;

  return (
    <>
      {fields.map((item, index) => {
        const fieldValue = fields.get(index);
        const {
          completed,
          companyId,
          accounts,
          permissions,
        } = fieldValue;

        const onlyUsefulPermissions = permissions && permissions.length > 0
          ? permissions.filter((permission) =>
            // permission.permissionName !== 'CreateShipment'
            permission.permissionName !== 'InterfaceSetting'
                 && permission.permissionName !== 'PrintLabels'
                 && permission.permissionName !== 'RequestPickup'
                 && permission.permissionName !== 'QuickEstimate') : permissions;
        const companyDetailsPermissions = companyDetails[index]
        && companyDetails[index].permissions.filter((permission) =>
          // permission.permissionName !== 'CreateShipment'
          permission.permissionName !== 'InterfaceSetting'
                   && permission.permissionName !== 'PrintLabels'
                   && permission.permissionName !== 'QuickEstimate'
                   && permission.permissionName !== 'RequestPickup');

        if (completed) {
          const companyName = companiesList.find((c) => c.companyId === companyId)?.companyName || companyId;
          return (
            <ContentBoxLightGrey key={index}>
              <CardHeader>
                <CardTitle>
                  {t('Company')}
                  {` ${index + 1}`}
                </CardTitle>
                <EditButtonGroup>
                  <LineButton onClick={() => onRemoveCompany(companyId, companyName)}>
                    <Icon className="icon" src={Delete} alt="delete" />
                    {t('Delete')}
                  </LineButton>
                  {state.showAddLineButton && (
                  <LineButton onClick={() => editUser(index, dispatch)}>
                    <Icon className="icon" src={Edit} alt="edit" />
                    {t('Edit')}
                  </LineButton>
                  )}
                </EditButtonGroup>
              </CardHeader>

              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('SelectedCompany')}</P>
                    <P className="text">{companyName}</P>
                  </TextProperty>
                </Column>
              </Row>
              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('BillingAccountNumber')}</P>
                    {accounts && accounts.map((a) =>
                      a.accountNumber
                      && (
                      <P key={a.accountNumber} className="text">
                        <img src={CheckMarkSolidOrange} alt="added item" />
                        {a.accountNumber}
                        {' '}
                        {`- ${a.accountType === 'PA'
                          ? t(BillingAccountType.Protected)
                          : t(BillingAccountType.Billing)}`}
                      </P>
                      ),
                    )}
                  </TextProperty>
                </Column>
              </Row>
              <Row>
                <Column>
                  <TextProperty className="no-margin">
                    <P className="title">{t('AssignedPermissions')}</P>
                    {onlyUsefulPermissions && onlyUsefulPermissions.length > 0 ? onlyUsefulPermissions.map((p) =>
                      p.permissionId
                      && (
                      <P key={p.permissionId} className="text">
                        <img src={CheckMarkSolidOrange} alt="added item" />
                        {p.permissionName && p.permissionName === 'ShipmentTracking'
                          ? 'Invoices' : p.permissionName && p.permissionName === 'ManageAddressBooks'
                            ? 'Manage Contacts' : p.permissionName && p.permissionName === 'ManageAccounts'
                              ? 'Manage Companies' : p.permissionName === 'CreateShipment' ? 'Create Shipment' : p.permissionName }
                      </P>
                      ),
                    ) : []}
                  </TextProperty>
                </Column>
              </Row>
            </ContentBoxLightGrey>
          );
        }

        return (
          <ContentBoxLightGrey className="edit-item-box" key={index}>
            <CardHeader>
              <CardTitle>
                {t('Company')}
                {` ${index + 1}`}
              </CardTitle>
            </CardHeader>
            <Row>
              <Column $tablet={6} $mobile={12}>
                <Field
                  name={`${item}.companyId`}
                  label={t('SelectedCompany')}
                  component={Dropdown}
                  options={
                    companiesList
                      .filter((c) => c.companyId === companyId
                        || !currentCompaniesList.some((cc) => cc.companyId === c.companyId && cc.completed))
                      .map((c) => ({ value: c.companyName, companyId: c.companyId }))
                  }
                  valueField="companyId"
                  valueFormatter={(opt) => (opt ? opt.value : '')}
                  onChange={(e) => getCompanyInfo(e, index)}
                  required
                  validate={[isRequired]}
                />
              </Column>
            </Row>
            {formData.companies && formData.companies[index] && formData.companies[index].companyId
              && (
              <>
                <Row>
                  <Column>
                    <TextProperty className="label">
                      <P className="text">{t('BillingAccountNumber')}</P>
                      <P className="title">{t('SelectBillingAccountForUser')}</P>
                    </TextProperty>
                  </Column>
                </Row>
                <CheckboxContainer className="multiple-checkbox">
                  <Field
                    name={`${item}.accounts`}
                    type="checkboxes"
                    label={t('SelectedAccountNumbers')}
                    data={companyDetails[index] && companyDetails[index].accounts}
                    renderer={companyCheckboxRenderer}
                    required
                    multiple
                    component={RenderField}
                    validate={[isRequired]}
                  />
                </CheckboxContainer>
                <Row>
                  <Column>
                    <TextProperty className="d-flex align-items-center">
                      <P className="text">{t('UserPermissions')}</P>
                      <Tooltip message={t('TipAddContact')} />
                    </TextProperty>
                  </Column>
                </Row>
                <CheckboxContainer className="multiple-checkbox">
                  <Field
                    name={`${item}.permissions`}
                    type="checkboxes"
                    label={t('SelectedPermissionNumbers')}
                    data={companyDetailsPermissions && companyDetailsPermissions.length > 0 ? companyDetailsPermissions : []}
                    renderer={companyCheckboxRenderer}
                    required
                    multiple
                    component={RenderField}
                    validate={[isRequired]}
                  />
                </CheckboxContainer>
                <AddItemContainer>
                  <SecondaryButton showArrow={false} onClick={() => cancelItem(index)}>
                    {t('Cancel')}
                  </SecondaryButton>
                  <PrimaryButton showArrow={false} disabled={disableAddItem} onClick={() => addItem(index)}>
                    {t('SaveItem')}
                  </PrimaryButton>
                </AddItemContainer>
              </>
              )}
          </ContentBoxLightGrey>
        );
      })}
      {state.showAddLineButton && (
        <FileUploadContainer className="with-dot">
          <FileUpload
            onClick={() => {
              if (!disableAddAnother) {
                fields.push({});
              }
            }}
            style={{
              pointerEvents: disableAddAnother ? 'none' : 'auto',
              opacity: disableAddAnother ? 0.5 : 1,
              cursor: disableAddAnother ? 'not-allowed' : 'pointer',
            }}
          >
            <img src={Plus} alt="add more part file" />
            <FileUploadLabel>{t('AddAnotherItem')}</FileUploadLabel>
          </FileUpload>
        </FileUploadContainer>
      )}
    </>
  );
};

const UserForm = ({
  fields, formData = {}, formName, dispatch, companiesList, deleteUser,
  getCompanyInfo, companyDetails, valid, showAdd, error, onRemoveCompany, onToggleUserStatus,
}) => {
  const {
    companies = [], rowVersion, email, oldPassword, disabled,
  } = formData;
  const {
    passwordImage,
  } = fields;
  const update = !!rowVersion;
  const isUserDisabled = disabled && disabled.length > 1;
  const hasManageCompanies = companiesList && companiesList.some(
    (company) => company.permissions && company.permissions.some(
      (p) => p.permissionName === 'ManageCompanies',
    ),
  );
  return (
    <>
      <PageTitle className="justified">
        {fields.pageTitle && fields.pageTitle.value}
      </PageTitle>
      <TextProperty>
        {fields.emailAddressInfo && fields.emailAddressInfo.value}
      </TextProperty>
      <Row>
        <Column $desktop={6} $tablet={8} $mobile={12}>
          {update
            ? (
              <TextProperty>
                <P className="title">{t('Email')}</P>
                <P className="text">{email}</P>
              </TextProperty>
            )
            : (
              <Field
                name="email"
                type="text"
                required
                component={TextInput}
                label={t('Email')}
                validate={[isRequired, isEmail]}
              />
            )}
        </Column>
      </Row>
      <CategoryTitle>
        {fields.profileDetailsTitle && fields.profileDetailsTitle.value}
      </CategoryTitle>
      <FormFields>
        <Row>
          <Column $tablet={8} $mobile={12}>
            <Field
              name="name"
              component={TextInput}
              label={t('FullName')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
              required
            />
          </Column>
          <Column $tablet={4} $mobile={12}>
            <Field
              name="title"
              component={TextInput}
              label={t('Title')}
              optional
              validate={[maxChar20]}
              maxLength={20}
            />
          </Column>
        </Row>
        <Row>
          <Column $desktop={6} $tablet={12} $mobile={12}>
            <Field
              name="companyName"
              component={TextInput}
              label={t('CompanyName')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
              required
            />
          </Column>
          <Column $desktop={4} $tablet={6} $mobile={8}>
            <Field
              name="phoneNumber"
              type="text"
              component={TextInput}
              label={t('PhoneNumber')}
              validate={[isPhone, maxChar50, isRequired]}
              normalize={phoneNumber}
              required
            />
          </Column>
          <Column $desktop={2} $tablet={6} $mobile={4}>
            <Field
              name="phoneNumberExt"
              type="text"
              component={TextInput}
              label={t('Extension')}
              validate={[maxChar5]}
              maxLength={5}
              normalize={digitOnly}
              optional
            />
          </Column>
        </Row>
        <Row className="justify-content-md-center">
          <Column>
            <AddressComplete
              fieldName="address"
              formName={formName}
              required
              tooltipRequired
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Field
              name="description"
              component={TextInput}
              label={t('Description')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
              required
            />
          </Column>
        </Row>
      </FormFields>
      <CategoryTitle>
        {fields.passwordTitle && fields.passwordTitle.value}
      </CategoryTitle>
      {update
        && (
        <>
          <TextProperty>
            {fields.passwordSummary && fields.passwordSummary.value}
          </TextProperty>
          <Row>
            <Column $desktop={6} $tablet={10} $mobile={12}>
              <Field
                name="oldPassword"
                type="password"
                required
                component={PasswordInput}
                label={t('OldPassword')}
                validate={[]}
              />
            </Column>
          </Row>
        </>
        )}
      {(!update || (update && oldPassword))
          && <PasswordGroup passwordImage={passwordImage} />}
      <TextProperty className="label">
        {fields.checkboxText && fields.checkboxText.value}
      </TextProperty>
      <CheckboxContainer>
        <Field
          name="changePassword"
          type="checkboxes"
          data={['Force User To Reset Password']}
          component={RenderField}
          multiple
        />
      </CheckboxContainer>
      <CategoryTitle>
        {fields.addUserTitle && fields.addUserTitle.value}
      </CategoryTitle>
      <ItemContainer>
        <FieldArray
          name="companies"
          dispatch={dispatch}
          component={renderUserCompanyList}
          valid={valid}
          formName={formName}
          companiesList={companiesList}
          currentCompaniesList={companies}
          getCompanyInfo={getCompanyInfo}
          companyDetails={companyDetails}
          formData={formData}
          showAdd={showAdd}
          error={error}
          onRemoveCompany={onRemoveCompany}
        />
      </ItemContainer>
      {hasManageCompanies && (
        <>
          <CategoryTitle>
            {fields.disableUserTitle && fields.disableUserTitle.value}
          </CategoryTitle>
          <TextProperty>
            {fields.disableUserText && fields.disableUserText.value}
          </TextProperty>
          {update && (
            <PrimaryButton
              type="button"
              className="active"
              showArrow={false}
              onClick={() => onToggleUserStatus(isUserDisabled)}
            >
              {isUserDisabled ? t('ActivateUser') : t('DeactivateUser')}
            </PrimaryButton>
          )}
        </>
      )}
      {
        update
        && (
        <>
          <CategoryTitle>
            {fields.deleteUserTitle && fields.deleteUserTitle.value}
          </CategoryTitle>
          <TextProperty>
            {fields.deleteUserText && fields.deleteUserText.value}
          </TextProperty>
          <PrimaryButton type="button" className={`${update && 'active'}`} showArrow={false} disabled={!update} onClick={() => deleteUser()}>
            {t('DeleteUser')}
          </PrimaryButton>
        </>
        )
      }
    </>
  );
};

UserForm.propTypes = {
  fields: PropTypes.object,
  formData: PropTypes.object,
  formName: PropTypes.string,
  dispatch: PropTypes.func,
  companiesList: PropTypes.array,
  deleteUser: PropTypes.func,
  getCompanyInfo: PropTypes.func,
  companyDetails: PropTypes.array,
  valid: PropTypes.bool,
  showAdd: PropTypes.bool,
  error: PropTypes.object,
  onRemoveCompany: PropTypes.func,
  onToggleUserStatus: PropTypes.func,
};

export default UserForm;
